<template>
  <b-container fluid class="p-0 bg-aba-secondary overflow-visible" id="tabnav">
    <b-container fluid tag="nav" class="p-0">
      <b-row no-gutters align-v="center">
        <b-col>
          <b-nav class="mb-0 d-flex align-items-center">
            <!-- Below `lg` the page links do not fit (400% zoom = ~320px viewport),
                 so they collapse into a burger dropdown named after the current
                 page (#20277). Same pattern as the talentpool portal. -->
            <b-nav-item-dropdown
              class="d-lg-none pages-nav__burger"
              toggle-class="pt-2 pb-2 text-white"
              menu-class="pages-nav__menu"
            >
              <template #button-content>
                <b-icon aria-hidden="true" icon="list" scale="1.2" class="mr-2 align-middle"></b-icon>
                <span class="align-middle">{{ activeNavigation || portalName }}</span>
              </template>
              <b-dropdown-item
                v-for="(item, index) in navigation"
                :key="`mobile-${index}`"
                :to="item.url"
                :active="item.url === $route.path"
                v-show="item.visible"
                @click="handleOpenTab($route.name, item.url === $route.path)"
              >
                <b-icon v-if="item.icon" aria-hidden="true" :icon="item.icon" scale="0.9" class="mr-1 align-middle"></b-icon>
                <span class="align-middle">{{ item.text }}</span>
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <!-- Not `d-none d-lg-block`: its `!important` would override the
                 inline `display: none` that `v-show` sets for hidden pages. -->
            <b-nav-item
              class="nav-item pages-nav__page"
              link-classes="pt-2 pb-2 text-white"
              :to="item.url"
              v-for="(item, index) in navigation"
              :key="index"
              :active="item.url === $route.path"
              @click="handleOpenTab($route.name, item.url === $route.path)"
              v-show="item.visible"
            >
              <b-icon v-if="item.icon" aria-hidden="true" :icon="item.icon" scale="0.9" class="mr-1 align-middle"></b-icon>
              <span class="align-middle">{{ item.text }}</span>
            </b-nav-item>
            <b-nav-item
              v-b-toggle.auth-sidebar
              v-if="isAuth"
              class="nav-item ml-auto"
              link-classes="pt-2 pb-2 px-2 text-white"
              href="#"
            >
              <b-icon aria-hidden="true" icon="person-circle" scale="0.8"></b-icon>
              <!-- Icon-only below `md`: keep the name for assistive tech -->
              <span class="d-none d-md-inline-block ml-2">{{ userDisplayName }}</span>
              <span class="sr-only d-md-none">{{ userDisplayName }}</span>
            </b-nav-item>
            <b-nav-item
              v-if="isAuth && hasFullAccess"
              class="nav-item"
              v-b-toggle.notebook-sidebar
              link-classes="pt-2 pb-2 text-white"
              :link-attrs="{ 'aria-label': `${translations[13795]} (${notebookLength})` }"
              id="journal-tooltip"
              href="#"
            >
              <span class="position-relative d-inline-block">
                <b-icon aria-hidden="true" icon="journal-bookmark-fill" class="d-block" scale="1.1" font-scale="0.98" />
                <span
                  style="top: -10px; right: -10px"
                  class="position-absolute start-100 translate-middle bg-danger rounded-circle p-1 mt-1 fs-7 lh-1"
                >
                  {{ notebookLength }}
                </span>
              </span>
              <b-tooltip target="journal-tooltip" left custom-class="custom-tooltip">{{ translations[13795] }}</b-tooltip>
            </b-nav-item>
            <b-nav-item
              v-if="!isAuth"
              class="nav-item ml-auto"
              link-classes="pt-2 pb-2 text-white"
              href="#"
              @click.prevent="authEvent"
              v-b-modal.login-modal
            >
              <b-icon aria-hidden="true" icon="power" scale="0.8"></b-icon>
              <span class="d-none d-md-inline-block ml-2">{{ translations[13716] }}</span>
              <span class="sr-only d-md-none">{{ translations[13716] }}</span>
            </b-nav-item>
          </b-nav>
        </b-col>
        <b-col cols="auto" class="ml-auto">
          <LanguageHeaderThemes />
        </b-col>
      </b-row>
    </b-container>
    <LoginModal />
    <RegisterModal />
    <TermsModal />
    <ResetPasswordModal modal-id="applicant-reset-password" user-type="applicant" />
    <ResetPasswordModal modal-id="company-reset-password" user-type="company" />
    <ResetPasswordModal modal-id="consultant-reset-password" user-type="consultant" />
  </b-container>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import AccessMixin from '@/mixins/AccessMixin';
import { AppModule, AuthTab, SettingsCounters } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import LoginModal from '@/components/Authorization/LoginModal.vue';
import RegisterModal from '@/components/Authorization/RegisterModal.vue';
import TermsModal from '@/components/Authorization/TermsModal.vue';
import ResetPasswordModal from '@/components/Authorization/ResetPasswordModal.vue';
import LanguageHeaderThemes from '@/components/Header/LanguageHeaderThemes.vue';
import { NotebookModule } from '@/store/modules/notebook';
import { WorkersModule } from '@/store/modules/workers';
import { ProfessionModule } from '@/store/modules/profession';
import { ClustersModule } from '@/store/modules/clusters';
import { CompanyModule } from '@/store/modules/company';
import { group } from '@/config/configuration';

@Component({
  components: {
    LoginModal,
    RegisterModal,
    TermsModal,
    LanguageHeaderThemes,
    ResetPasswordModal,
  },
})
export default class PagesNavigation extends mixins(AccessMixin) {
  get translations() {
    return AppModule.translation;
  }

  get portalName() {
    return AppModule.settingPortal.portal_name;
  }

  get isAuth() {
    return UserModule.isUserAuth;
  }

  // TokenApplicant
  get accessTokenApplicant() {
    return UserModule.accessTokenApplicant;
  }

  // TokenConsultant
  get accessTokenConsultant() {
    return UserModule.accessTokenConsultant;
  }

  // TokenCompany
  get accessTokenCompany() {
    return UserModule.accessTokenCompany;
  }

  get userInfoApplicant() {
    return UserModule.userInfoApplicant;
  }

  get userInfoConsultant() {
    return UserModule.userInfoConsultant;
  }

  get userInfoCompany() {
    return UserModule.userInfoCompany;
  }

  // The store types `userInfo*` as `null` literals, hence the casts.
  get userDisplayName() {
    const applicant = (this.userInfoApplicant as any)?.applicant;
    if (this.accessTokenApplicant && applicant?.firstname) {
      return `${applicant.firstname} ${applicant.lastname || ''}`;
    }
    const consultant = (this.userInfoConsultant as any)?.consultant;
    if (this.accessTokenConsultant && consultant) {
      return `${consultant.firstname} ${consultant.lastname || ''}`;
    }
    const company = (this.userInfoCompany as any)?.company;
    if (this.accessTokenCompany && company) {
      return company.company_name || '';
    }
    return '';
  }

  get notebookLength() {
    return Number(NotebookModule.notebookJobs.length) + Number(NotebookModule.notebookWorkers.length) + Number(NotebookModule.notebookCompany.length);
  }

  get sid() {
    return AppModule.sid;
  }

  get isHide() {
    return !group(this.sid);
  }

  get getCounters() {
    return AppModule.settingPortal.counters || [];
  }

  isShowPage(value: SettingsCounters['name']) {
    return this.getCounters.find((val) => val.name === value || (value === 'maps' && (val.name as string) === 'map'))?.visible;
  }

  get navigation() {
    return [
      {
        url: '/',
        text: this.translations[13801],
        visible: true,
        icon: 'house-door-fill',
      },
      {
        url: '/jobs',
        text: this.translations[12604],
        visible: this.isShowPage('jobs'),
      },
      {
        url: '/company',
        text: this.translations[13937],
        visible: this.isShowPage('companies'),
      },
      {
        url: '/workers',
        text: this.translations[4741],
        visible: this.isShowPage('applicants'),
      },
      {
        url: '/map',
        text: this.translations[13719],
        visible: this.isShowPage('maps'),
      },
    ];
  }

  get activeNavigation() {
    return this.navigation.find((x) => this.$route.path === x.url)?.text;
  }

  authEvent() {
    UserModule.IS_REDIRECT_TO_CABINET(true);
  }

  handleOpenTab(currentRoute: string, isActive: boolean) {
    // reset every tab here;
    if (isActive) {
      switch (currentRoute) {
        case 'Jobs':
          AppModule.SetVisibleJobs(false);
          AppModule.SetVisibleMap(false);
          AppModule.SetVisibleClusterJobs(false);
          ProfessionModule.SetParamsDefault();
          ProfessionModule.SetTagsDefault();
          ClustersModule.ResetClusterData();
          break;
        case 'Workers':
          WorkersModule.resetAllWorkersFilters();
          break;
        case 'Company':
          CompanyModule.SetParamsDefaultCompany();
          CompanyModule.SET_SELECTED_COMPANIES([]);
          AppModule.SetVisibleCompany(false);
          break;
        default:
          break;
      }
    }
  }

  handleAuthQuery() {
    const query = this.$route.query.auth;
    let selectedAuthTab = AuthTab.applicant;
    let setRegistrationTab = false;

    switch (query) {
      case 'a-login':
        break;
      case 'f-login':
        selectedAuthTab = AuthTab.firm;
        break;
      case 'a-registration':
        setRegistrationTab = true;
        break;
      case 'f-registration':
        selectedAuthTab = AuthTab.firm;
        setRegistrationTab = true;
        break;
      default:
        return;
    }

    AppModule.SET_SELECTED_AUTH_TAB(selectedAuthTab);

    if (setRegistrationTab) {
      this.$bvModal.show('register-modal');
      return;
    }

    this.$bvModal.show('login-modal');
  }

  // vue-router sets `aria-current` itself, but only when `isSameRoute()` matches,
  // and that comparison includes the query string. Every route here carries a
  // `lang` param while `item.url` is a bare path, so it never matches and the
  // link renders `aria-current: null`. Handing the attribute down through
  // `link-attrs` does not help either: `<router-link>` rewrites it on its own
  // element on every render and wins over the inherited attribute. So mark the
  // current page on the mounted DOM, the same way `AppPagination` does, reading
  // the state from the `active` class that BootstrapVue puts on the link.
  markCurrentPage() {
    Array.from(this.$el.querySelectorAll('.nav-link, .dropdown-item')).forEach((link) => {
      if (link.classList.contains('active')) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  // TODO: maybe move to app? but no need with iframe
  mounted() {
    this.markCurrentPage();

    if (!this.accessTokenApplicant && !this.accessTokenCompany && !this.accessTokenConsultant) {
      this.handleAuthQuery();
    }
  }

  // Runs after the router links have re-rendered on a route change
  updated() {
    this.markCurrentPage();
  }
}
</script>
